import { useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import LookupSelect, { LookupOption } from '../../components/LookupSelect';

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

const todayMinus31 = () => {
  const d = new Date();
  d.setDate(d.getDate() - 31);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

export default function UpdateByIndividual() {
  const [employeeId, setEmployeeId] = useState('');
  const [fromDate, setFromDate] = useState(todayMinus31());
  const [toDate, setToDate] = useState(today());
  const [filter, setFilter] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState<Record<string, any>>({});

  const fetchRows = async () => {
    if (!employeeId) return alert('Select an employee');
    const days = (new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86400000;
    if (days > 62) return alert('Range is limited to 62 days');
    setLoading(true);
    try {
      const res = await api.get('/tms/attendance/individual', { params: { employeeId, fromDate, toDate, filter: filter || undefined } });
      setRows(res.data.attendance || []);
      setEdits({});
    } finally {
      setLoading(false);
    }
  };

  const createMissingDates = async () => {
    if (!employeeId) return alert('Select an employee');
    await api.post('/tms/attendance/create-missing-dates', { employeeId, fromDate, toDate });
    fetchRows();
  };

  const setEdit = (id: string, field: string, value: any) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveRow = async (row: any) => {
    const change = edits[row.id];
    if (!change) return;
    await api.put(`/tms/attendance/${row.id}`, change);
    fetchRows();
  };

  const toggleLock = async (row: any) => {
    await api.post(`/tms/attendance/${row.id}/${row.is_locked ? 'unlock' : 'lock'}`);
    fetchRows();
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Update by Individual</h1>

      <div className="bg-white rounded-lg shadow p-4 mb-4 flex gap-3 items-end flex-wrap">
        <LookupSelect label="Employee" value={employeeId} onChange={setEmployeeId} fetchOptions={fetchEmployeeOptions} placeholder="Search employee..." required />
        <Input label="From Date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <Input label="To Date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        <Select
          label="Filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          options={[
            { value: 'late', label: 'Late' },
            { value: 'under', label: 'Under' },
            { value: 'abs', label: 'Absent' },
            { value: 'insufficient', label: 'Insufficient Hours' },
          ]}
        />
        <Button onClick={fetchRows} loading={loading}>Load</Button>
        <Button variant="secondary" onClick={createMissingDates}>Create Missing Dates</Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="p-2 text-left">Date</th>
              <th className="p-2">Day</th>
              <th className="p-2">Clk1N</th>
              <th className="p-2">Clk2N</th>
              <th className="p-2">Clk3N</th>
              <th className="p-2">Clk4N</th>
              <th className="p-2">In</th>
              <th className="p-2">Out</th>
              <th className="p-2">Reason</th>
              <th className="p-2">NRM</th>
              <th className="p-2">Lateness</th>
              <th className="p-2">Under</th>
              <th className="p-2">OT1.0</th>
              <th className="p-2">OT1.5</th>
              <th className="p-2">OT2.0</th>
              <th className="p-2">OT3.0</th>
              <th className="p-2">Flat</th>
              <th className="p-2">PH/RES</th>
              <th className="p-2">Lock</th>
              <th className="p-2">Save</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const rowEdits = edits[r.id] || {};
              const val = (field: string, column: string) => (rowEdits[field] !== undefined ? rowEdits[field] : r[column]);
              return (
                <tr key={r.id} className={`border-b ${r.is_locked ? 'bg-gray-100' : ''}`}>
                  <td className="p-2 whitespace-nowrap">{r.transaction_date?.slice(0, 10)}</td>
                  <td className="p-2">{r.day_of_week?.slice(0, 3)}</td>
                  <td className="p-2">{r.actual_clock_in_1 || '—'}</td>
                  <td className="p-2">{r.actual_clock_in_2 || '—'}</td>
                  <td className="p-2">{r.actual_clock_in_3 || '—'}</td>
                  <td className="p-2">{r.actual_clock_in_4 || '—'}</td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-20 border rounded px-1" value={val('clockInTime', 'clock_in_time') || ''} onChange={(e) => setEdit(r.id, 'clockInTime', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-20 border rounded px-1" value={val('clockOutTime', 'clock_out_time') || ''} onChange={(e) => setEdit(r.id, 'clockOutTime', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-16 border rounded px-1" value={val('reasonCode', 'reason_code') || ''} onChange={(e) => setEdit(r.id, 'reasonCode', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-14 border rounded px-1" value={val('normalWorkingHours', 'normal_working_hours') ?? ''} onChange={(e) => setEdit(r.id, 'normalWorkingHours', e.target.value)} /></td>
                  <td className={`p-1 ${r.lateness_minutes > 0 ? 'text-red-600' : ''}`}><input disabled={r.is_locked} className="w-14 border rounded px-1" value={val('latenessMinutes', 'lateness_minutes') ?? ''} onChange={(e) => setEdit(r.id, 'latenessMinutes', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-14 border rounded px-1" value={val('underMinutes', 'under_minutes') ?? ''} onChange={(e) => setEdit(r.id, 'underMinutes', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-14 border rounded px-1" value={val('ot10Hours', 'ot_1_0_hours') ?? ''} onChange={(e) => setEdit(r.id, 'ot10Hours', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-14 border rounded px-1" value={val('ot15Hours', 'ot_1_5_hours') ?? ''} onChange={(e) => setEdit(r.id, 'ot15Hours', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-14 border rounded px-1" value={val('ot20Hours', 'ot_2_0_hours') ?? ''} onChange={(e) => setEdit(r.id, 'ot20Hours', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-14 border rounded px-1" value={val('ot30Hours', 'ot_3_0_hours') ?? ''} onChange={(e) => setEdit(r.id, 'ot30Hours', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-14 border rounded px-1" value={val('flatOtHours', 'flat_ot_hours') ?? ''} onChange={(e) => setEdit(r.id, 'flatOtHours', e.target.value)} /></td>
                  <td className="p-1"><input disabled={r.is_locked} className="w-14 border rounded px-1" value={val('phRestDays', 'ph_rest_days') ?? ''} onChange={(e) => setEdit(r.id, 'phRestDays', e.target.value)} /></td>
                  <td className="p-2 text-center">
                    <button onClick={() => toggleLock(r)} className={`px-2 py-1 rounded text-xs ${r.is_locked ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                      {r.is_locked ? 'Locked' : 'Unlocked'}
                    </button>
                  </td>
                  <td className="p-2 text-center">
                    <Button variant="secondary" onClick={() => saveRow(r)}>Save</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!rows.length && <div className="p-6 text-center text-gray-500 text-sm">No records loaded — select an employee and Load.</div>}
      </div>
    </div>
  );
}
